"use client";

import { useEffect, useRef, useMemo, useState } from "react";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { Heading } from "@repo/ui/heading";
import BookingStays from "../booking-stay/BookingStay";

type StrapiVideo = {
  url?: string | null;
  mime?: string | null;
  alternativeText?: string | null;
  captions?: {
    url?: string | null;
    srcLang?: string | null;
    label?: string | null;
  } | null;
};

type HeroMainProps = {
  title?: string | null;
  description?: any;
  videoURL?: string | null;
  video?: StrapiVideo | null;
};

function resolveCmsUrl(raw?: string | null) {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (process.env.NEXT_PUBLIC_STRAPI_URL || "").replace(/\/+$/, "");
  const rel = raw.startsWith("/") ? raw : `/${raw}`;
  return base ? `${base}${rel}` : rel;
}

export default function HeroSection({
  title,
  description,
  videoURL,
  video,
}: HeroMainProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const src = useMemo(() => {
    const raw = (videoURL && videoURL.trim()) || video?.url || null;
    return resolveCmsUrl(raw);
  }, [videoURL, video?.url]);

  const typeAttr = video?.mime || undefined;

  // Intersection observer for lazy loading video
  useEffect(() => {
    if (!src) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    const heroSection = document.querySelector('.hero-section');
    if (heroSection) {
      observer.observe(heroSection);
    }

    return () => observer.disconnect();
  }, [src]);

  useEffect(() => {
    if (!src) return;
    const v = videoRef.current;
    if (!v) return;

    v.muted = true; // set properties early
    // v.playsInline = true;   // React already set via prop

    v.setAttribute("referrerpolicy", "no-referrer");

    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (m.matches) {
        // Respect user setting: pause, but avoid removing attributes permanently
        v.pause();
      } else {
        // Try to start if paused and allowed
        if (v.paused) {
          const tryPlay = () => v.play().catch(() => {});
          // Nudge playback once metadata is ready
          if (v.readyState >= 1) tryPlay();
          else v.addEventListener("loadedmetadata", tryPlay, { once: true });
        }
      }
    };

    apply();
    const onChange = () => apply();
    // cross-browser mql change handling
    if (m.addEventListener) m.addEventListener("change", onChange);
    else m.addListener(onChange);

    return () => {
      if (m.removeEventListener) m.removeEventListener("change", onChange);
      else m.removeListener(onChange);
    };
  }, [src]);

  const headingId = "hero-title";

  return (
    <>
      <section
        className="hero-section flex w-full h-screen z-10 relative"
        aria-labelledby={headingId}
        role="region"
      >
        {/* Background layers */}
        <div className="bg-gradient-to-b from-black to-transparent w-full h-full top-0 left-0 absolute opacity-60 -z-10" />

        {src && (
          <div className="absolute w-full overflow-hidden h-full -z-20">
            {shouldLoadVideo ? (
              <video
                key={src}
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={src}
                autoPlay
                loop
                muted
                playsInline
                aria-label="Background video"
                preload="metadata"
                poster=""
              >
              {typeAttr ? <source src={src} type={typeAttr} /> : null}
                <track 
                  kind="captions" 
                  srcLang={video?.captions?.srcLang || "en"} 
                  label={video?.captions?.label || "English captions"} 
                  src={video?.captions?.url ? resolveCmsUrl(video.captions.url) || "" : ""} 
                />
              </video>
            ) : (
              <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-black/20 to-black/60" />
            )}
          </div>
        )}

        <div className="flex flex-col mt-62 relative mx-auto w-full max-w-3xl gap-12">
          <div className="text-center flex flex-col gap-8 xl:px-0 px-10 max-w-2xl mx-auto">
            <Heading
              id={headingId}
              className="hero-text xl:text-6xl md:text-5xl text-4xl text-white tracking-tighter"
              level="h1"
            >
              {title || "Switzerland's new hospitality landmark"}
            </Heading>

            <div className="sm:text-lg text-white md:px-24 sm:px-14 px-4">
              {Array.isArray(description) ? (
                <BlocksRenderer content={description} />
              ) : (
                <p>
                  {description ||
                    "Swiss precision meets modern comfort — perfectly balanced for work and rest in prime locations."}
                </p>
              )}
            </div>
          </div>

          <div aria-label="Check availability">
            <BookingStays />
          </div>
        </div>
      </section>
    </>
  );
}
